import { ComponentRef } from '@angular/core';
import { ModalBackdrop } from '../components/backdrop/backdrop.component';
import { ModalWindow } from '../components/window/window.component';
import { ContentRef } from './content-ref';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/operator/first';
import 'rxjs/add/observable/from';
import 'rxjs/add/observable/of';
import { from } from 'rxjs';


export class ModalRef {

    private resolve: (result?: any) => void;

    private reject: (reason?: any) => void;

    private closingCallbacks: Array<any> = [];

    private dismissingCallbacks: Array<any> = [];

    private closedCallbacks: Array<any> = [];

    private dismissedCallbacks: Array<any> = [];

    get instance(): any {
        if (this.contentRef.componentRef) {
            return this.contentRef.componentRef.instance;
        }
    }

    public result: Promise<any>;

    constructor(private windowComponentRef: ComponentRef<ModalWindow>, private contentRef: ContentRef, private backdropComponentRef?: ComponentRef<ModalBackdrop>) {
        windowComponentRef.instance.dismissEvent.subscribe((reason: any) => {
            this.dismiss(reason);
        });

        this.result = new Promise((resolve, reject) => {
            this.resolve = resolve;
            this.reject  = reject;
        });

        this.result.then((result) => {
            this.executeCallbacks(this.closedCallbacks, result, false).catch(() => {
            });
        }, (reason) => {
            this.executeCallbacks(this.dismissedCallbacks, reason, false).catch(() => {
            });
        });
    }

    /**
     * Add a before close callback.
     *
     * @deprecated Use closing
     * @param {(resolve: (result: any) => void, reject?: (error: any) => void, result?: any) => void} callback
     */
    public beforeClose(callback: (resolve: (result?: any) => void, reject?: (error?: any) => void, result?: any) => void) {
        this.closingCallbacks = this.closingCallbacks.concat(callback);
    }

    /**
     * Add a before close callback.
     *
     * @param {(resolve: (response: any) => void, reject?: (error: any) => void, response?: any) => void} callback
     */
    public closing(callback: (resolve: (response?: any) => void, reject?: (error?: any) => void, response?: any, res?: (response?: any) => void, rej?: (error?: any) => void,) => void) {
        this.closingCallbacks = this.closingCallbacks.concat(callback);
    }

    /**
     * Add a on closed callback.
     *
     * @param {(response?: any) => void} callback
     */
    public closed(callback: (response?: any) => void) {
        this.closedCallbacks.push(callback);
    }

    /**
     * Add a before dismiss callback.
     *
     * @deprecated use dismissing
     * @param {(resolve: (result: any) => void, reject?: (error: any) => void, reason?: any) => void} callback
     */
    public beforeDismiss(callback: (resolve: (reason?: any) => void, reject?: (error?: any) => void, reason?: any) => void) {
        this.dismissingCallbacks = this.dismissingCallbacks.concat(callback);
    }

    /**
     * Add a before dismiss callback.
     *
     * @param {(resolve: (reason: any) => void, reject?: (error: any) => void, reason?: any) => void} callback
     */
    public dismissing(callback: (resolve: (reason?: any) => void, reject?: (error?: any) => void, reason?: any) => void) {
        this.dismissingCallbacks = this.dismissingCallbacks.concat(callback);
    }

    /**
     * Add a on dismissed callback.
     *
     * @param {(reason?: any) => void} callback
     */
    public dismissed(callback: (reason?: any) => void) {
        this.dismissedCallbacks.push(callback);
    }

    /**
     * Can be used to close a modal, passing an optional result.
     *
     * @param result
     */
    public close(result?: any): void {
        if (this.windowComponentRef) {
            this.executeBeforeCloseCallbacks(result).then((result) => {
                this.resolve(result);
                this.removeModalElements();
            }, (error) => {
            });
        }
    }

    /**
     * Execute the list of before close callbacks and return a promise
     * which indicates if all callbacks have passed.
     *
     * @param result
     * @return {Promise<any>}
     */
    private executeBeforeCloseCallbacks(result?: any) {
        return this.executeCallbacks(this.closingCallbacks, result);
    }

    /**
     * Can be used to dismiss a modal, passing an optional reason.
     *
     * @param reason
     */
    public dismiss(reason?: any): void {
        if (this.windowComponentRef) {
            this.executeBeforeDismissCallbacks(reason).then((reason) => {
                this.reject(reason);
                this.removeModalElements();
            }, (error) => {
                console.error(error);
                this.reject(error);
            });
        }
    }

    /**
     * Execute the list of before dismiss callbacks and return a promise
     * which indicates if all callbacks have passed.
     *
     * @param result
     * @return {Promise<any>}
     */
    private executeBeforeDismissCallbacks(result?: any) {
        return this.executeCallbacks(this.dismissingCallbacks, result);
    }

    /**
     * Execute a list of callbacks and return a promise which indicates
     * if all callbacks have passed.
     *
     * @param {Array<Function>} callbacks
     * @param result
     * @param rejectable
     * @return {Promise<any>}
     */
    private executeCallbacks(callbacks: Array<Function>, result?: any, rejectable: boolean = true) {
        return new Promise((res, rej) => {
            if (!callbacks.length) {
                return res(result);
            }

            let i = 1;

            // Create an array of observables
            const observable = from(callbacks);

            // The resolve closure which will check if all callbacks
            // have been resolved, if so it will resolve the promise
            const resolve = (result: any) => {
                if (i === callbacks.length) {
                    res(result);
                }
                i++;
            };

            // The reject closure which triggers rejects the promise
            const reject = (error: any) => {
                rej(error);
                i++;
            };

            // Execute all callbacks and reject the promise if an
            // error occurs.
            observable.subscribe((callback) => {
                if (rejectable) {
                    callback(resolve, reject, result);
                } else {
                    callback(result);

                    resolve(result);
                }
            }, (error) => {
                rej(error);
            });
        });
    }

    private removeModalElements() {
        this.removeWindowElements();

        if (this.backdropComponentRef) {
            this.removeBackdropElements();
        }

        if (this.contentRef && this.contentRef.viewRef) {
            this.removeContent();
        }
    }

    private removeWindowElements() {
        const windowNativeEl = this.windowComponentRef.location.nativeElement;
        windowNativeEl.parentNode.removeChild(windowNativeEl);

        this.windowComponentRef.destroy();
        this.windowComponentRef = null;
    }

    private removeBackdropElements() {
        const backdropNativeEl = this.backdropComponentRef.location.nativeElement;
        backdropNativeEl.parentNode.removeChild(backdropNativeEl);

        this.backdropComponentRef.destroy();
        this.backdropComponentRef = null;
    }

    private removeContent() {
        this.contentRef.viewRef.destroy();
        this.contentRef = null;
    }

}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/modals/classes/modal-ref.ts